import {
  ChatActionsResponse,
  ErrorPayload,
  ChatSummaryItem,
  ChatSettings,
  ChatMessageResponse,
  FilePayload,
  ChatAppResponse,
  ChatResponse,
} from '@worknet/models';
import { create } from 'zustand';
import { devtools, persist, createJSONStorage } from 'zustand/middleware';

import { HostPlatformContext } from './api';
import { getAllQueryValues } from './urlParamsValues';
import { getCachedConvId, cacheConvId, clearCachedConvId } from './convCache';
import { StripAllParser } from './parsers';
import { processClientAction } from './clientActions';
import { WorknetClientError } from './errorTypes';

function generateCorrelationId(): string {
  return crypto.randomUUID();
}

type AsyncState<T> = {
  loading?: boolean;
  // error?: Error; // error removed from here and managed on the global store
  value?: T;
};

type ProgressingBotMessage = { replyToMessageId: string; content: string };
interface State {
  hostPlatformContext?: HostPlatformContext;
  app: AsyncState<ChatSettings & ChatActionsResponse & ChatAppResponse>;
  chats: ChatSummaryItem[];
  initializing: boolean;
  chat?: AsyncState<{
    chat?: ChatResponse;
    messages: ChatMessageResponse[];
    progressingBotMessage?: ProgressingBotMessage;
    sending?: boolean;
    sharingConfirmed?: boolean;
  }>;
  error?: Error;
  uploadWarning?: string;
  pendingText?: string;
}

type ChatMessageInfo = {
  display?: string;
  content: string;
  actionId: string;
  attachments?: File[];
  isAutoMessage?: boolean;
};

interface Actions {
  init(hostPlatformContext: HostPlatformContext, refresh?: boolean): void;
  openChat(chatId?: string): void;
  startNewChat(): void;
  closeChat(): void;
  sendMessage(info: ChatMessageInfo): void;
  setChatSharingConfirmed(sharingConfirmed: boolean): void;
  setMessageLiked(
    messageId: string,
    isLiked: boolean,
    comment: string,
    onSuccess: (isLiked: boolean) => void
  ): void;
  getFileMetadata(fileId: string): Promise<FilePayload>;
  getSourceFile(chatId: string, messageId: string, sourceId: string): Promise<FilePayload>;
}

type Store = State & Actions;

const { widgetKey = '', deviceId = '' } = getAllQueryValues();

const useChatStore = create<Store>()(
  persist(
    devtools((set, get): Store => {
      function wrapHandler<T extends object>(
        callback: (payload: T) => void,
        onError: (error: WorknetClientError) => void = (error) => {
          set({ error });
        }
      ) {
        return async function (payload: T | ErrorPayload) {
          if ('error' in payload) {
            console.warn(payload);

            const error = new Error(payload.error.message) as WorknetClientError;
            error.code = payload.error.code;
            error.metadata = payload.error.metadata;
            onError(error);
          } else {
            return callback(payload);
          }
        };
      }

      let contentParser: StripAllParser | undefined = undefined;

      function isHostedConversationCacheEnabled() {
        const appValue = get().app.value;
        return !!appValue?.amaAction?.useNewAiLayer && appValue?.type !== 'widget';
      }

      function getConversationCacheKeyParts() {
        const hostPlatformContext = get().hostPlatformContext;
        const userId = hostPlatformContext?.user?.sub ?? hostPlatformContext?.user?.email;
        return {
          userId,
          groupingId: hostPlatformContext?.groupingId,
        };
      }

      function maybeGetCachedConversationId() {
        if (!isHostedConversationCacheEnabled()) {
          return undefined;
        }
        const { userId, groupingId } = getConversationCacheKeyParts();
        return getCachedConvId(userId, groupingId);
      }

      function maybeCacheConversation(chatId: string) {
        if (!isHostedConversationCacheEnabled()) {
          return;
        }
        const { userId, groupingId } = getConversationCacheKeyParts();
        cacheConvId(userId, groupingId, chatId);
      }

      function maybeClearConversationCache() {
        if (!isHostedConversationCacheEnabled()) {
          return;
        }
        const { userId, groupingId } = getConversationCacheKeyParts();
        clearCachedConvId(userId, groupingId);
      }

      function init(hostPlatformContext: HostPlatformContext, refresh?: boolean) {
        const currentState = get();
        const previousChat = currentState.chat?.value?.chat;
        const previousChatId = previousChat && previousChat.canReload ? previousChat.id : undefined;
        if (refresh) {
          set(() => ({
            hostPlatformContext: undefined,
            app: { loading: true },
            chats: [],
            chat: undefined,
            error: undefined,
          }));
        }

        // Init is called twice in dev cos of useEffect in HostedApp, making all on handlers register twice, meaning they are fired twice on incoming event
        if (get().hostPlatformContext) {
          return;
        }

        set(() => ({ hostPlatformContext, initializing: true }));

        const { socket, user } = hostPlatformContext;

        if (!refresh) {
          socket.on('connect', () => {
            init(hostPlatformContext, true);
          });

          socket.on('connect_error', (error) => {
            set({ error: new Error(error.message) });

            const context = 'context' in error ? error.context : undefined;
            const xhr = context instanceof XMLHttpRequest ? (context as XMLHttpRequest) : undefined;
            console.error(`Connection error ${error.message}`, {
              error,
              responseText: xhr?.responseText,
              status: xhr?.statusText,
            });
          });

          socket.on(
            'error',
            wrapHandler(() => {})
          );

          socket.on(
            'chat-summaries:changed',
            wrapHandler((payload) => {
              set((state) => {
                const chats = state.chats.slice();
                if (!payload.shared) {
                  return { chats };
                }

                const index = chats.findIndex((c) => c.id === payload.id);
                if (index !== -1) {
                  chats[index] = payload;
                } else {
                  chats.unshift(payload);
                }
                return { chats: chats.toSorted(chatSorter) };
              });
            })
          );

          socket.on(
            'chat-msg:from-ai:progress',
            wrapHandler(
              (payload) => {
                set((state) => {
                  const chat = state.chat?.value;
                  if (!chat) {
                    return {};
                  }
                  if ('error' in payload) {
                    return {};
                  }

                  if ('type' in payload && payload.type === 'clientAction') {
                    processClientAction(payload.action);
                    return {};
                  }

                  const progressingBotMessage = (() => {
                    if (!('type' in payload)) {
                      return payload;
                    } else if (payload.type === 'aggregatedTextResponse') {
                      return {
                        replyToMessageId: payload.replyToMessageId,
                        content: payload.content,
                      };
                    } else {
                      console.log('payload', payload);
                    }
                    return state?.chat?.value?.progressingBotMessage;
                  })();

                  if (progressingBotMessage) {
                    contentParser ??= new StripAllParser();
                    progressingBotMessage.content = contentParser.handleNewContent(
                      progressingBotMessage.content
                    );
                  }

                  return {
                    chat: {
                      value: {
                        ...chat,
                        progressingBotMessage,
                      },
                    },
                  };
                });
              },
              (error) => {
                set((state) => {
                  const chat = state.chat?.value;
                  if (!chat) {
                    return { error };
                  }
                  return {
                    error,
                    chat: { value: { ...chat, progressingBotMessage: undefined } },
                  };
                });
              }
            )
          );

          socket.on(
            'chat-msg:created',
            wrapHandler((payload) => {
              contentParser = new StripAllParser();
              set((state) => {
                const { message, chat: updatedChat } = payload;
                const chat = state.chat?.value;
                if (!chat || !chat.chat || chat.chat.id !== message.chatId) {
                  return {};
                }
                if (message.role === 'ai' && 'error' in message.content) {
                  console.warn(message.content.error);
                }

                return {
                  chat: {
                    value: {
                      ...chat,
                      chat: {
                        ...chat.chat,
                        otherUsers: updatedChat.otherUsers,
                        totalAttachmentSize: updatedChat.totalAttachmentSize,
                        totalAttachmentTokens: updatedChat.totalAttachmentTokens,
                      },
                      ...(message.role === 'ai' &&
                      chat.progressingBotMessage?.replyToMessageId === message.replyToMessageId
                        ? { progressingBotMessage: undefined }
                        : {}),
                      messages: [...chat.messages, message],
                    },
                  },
                };
              });
            })
          );
        }
        socket.emit(
          'app:init',
          {
            sourceContext: hostPlatformContext.sourceContext,
            customContext: hostPlatformContext.customContext,
            groupingId: hostPlatformContext.groupingId,
            embedderInfo: hostPlatformContext.embedderInfo,
            user: {
              name: user?.name,
              picture: user?.picture,
              source: user ? { system: 'auth0', id: user?.sub } : undefined, //TODO: check where user auth is from when add another way to authenticate
            },
            correlationId: generateCorrelationId(),
          },
          wrapHandler((payload) => {
            set(() => ({
              app: { value: { ...payload.app, ...payload.actions, ...payload.settings } },
              chats: payload.summaries?.toSorted(chatSorter),
            }));

            const isNewAiLayer = !!payload.actions?.amaAction?.useNewAiLayer;
            const persistConversation = payload.settings?.persistConversation !== false;
            const shouldRestoreChat = !!refresh || !isNewAiLayer || persistConversation;

            openChat(
              shouldRestoreChat ? (previousChatId ?? maybeGetCachedConversationId()) : undefined,
              {
                runAuto: true,
                forceNew: !!(
                  hostPlatformContext.nudge?.input ||
                  hostPlatformContext.nudge?.display ||
                  hostPlatformContext.initialMessage
                ),
              }
            );
            set({ initializing: false });
          })
        );
      }

      function openChat(
        chatId?: string,
        opts: { runAuto: boolean; forceNew: boolean; isNewConversation?: boolean } = {
          runAuto: false,
          forceNew: false,
        }
      ) {
        const {
          app: { value: appValue },
          sendMessage,
          hostPlatformContext,
          chat,
        } = get();

        if (!hostPlatformContext) {
          throw new Error('Init must be called before opening chat');
        }

        const forceNewChat = opts.runAuto && opts.forceNew;

        if (chatId && !forceNewChat) {
          if (!chat || chat.value?.chat?.id !== chatId) {
            set({ chat: { loading: true } });
          }
          hostPlatformContext.socket.emit(
            'chat:load',
            { chatId, correlationId: generateCorrelationId() },
            wrapHandler(
              (payload) => {
                maybeCacheConversation(payload.chat.id);
                set((state) => ({
                  chat: {
                    value: {
                      ...state.chat?.value,
                      ...payload,
                      progressingBotMessage: undefined,
                    },
                  },
                }));
              },
              () => {
                maybeClearConversationCache();
                openChat(undefined, { runAuto: true, forceNew: false });
              }
            )
          );
        } else {
          set({ chat: { value: { messages: [], sharingConfirmed: true } } });

          if (opts.runAuto) {
            const nudge = hostPlatformContext.nudge;
            const autoAction = appValue?.autoAction;

            if (appValue?.amaAction.useNewAiLayer) {
              // A host supplied message is a one-shot for the initial open: it is never replayed on
              // "New Conversation", and it is sent regardless of the autoFirstReply setting.
              const hostMessage = opts.isNewConversation
                ? undefined
                : (hostPlatformContext.initialMessage ?? nudge?.input);
              const display = opts.isNewConversation ? undefined : nudge?.display;
              const autoFirstReplyEnabled = appValue.autoFirstReply !== false;

              if (!hostMessage && !display && !autoFirstReplyEnabled) {
                // Fall through to the welcome message and wait for the user.
                return;
              }

              return sendMessage({
                actionId: appValue.amaAction.id,
                content: hostMessage ?? '',
                display,
              });
            } else if (autoAction && !opts.isNewConversation) {
              return sendMessage({
                actionId: autoAction.id,
                content: autoAction.displayName,
                isAutoMessage: true,
              });
            }
          }
        }
      }

      function startNewChat() {
        openChat(undefined, { runAuto: true, forceNew: false, isNewConversation: true });
      }

      function closeChat() {
        if (!get().chats?.length) {
          // TODO: is this a ui issue?
          throw new Error('Cannot close chat when there are no sessions');
        }
        set({ chat: undefined });
      }

      function sendMessage({
        actionId,
        content: message,
        display,
        attachments,
        isAutoMessage,
      }: ChatMessageInfo) {
        const { chatValue, socket } = getActiveChat(get());

        set((state) => ({
          chat: {
            value: {
              ...state.chat!.value!,
              sending: true,
            },
          },
        }));

        // TODO: add timeout

        const uploadFiles = async () => {
          if (!attachments || attachments.length === 0) {
            return [];
          }

          const uploadPromises = attachments.map((file) => {
            return new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => {
                if (reader.result === null) {
                  reject(new Error('File read resulted in null content'));
                  return;
                }
                socket.emit(
                  'chat.attachment-upload',
                  {
                    content: reader.result,
                    contentType: file.type,
                    fileName: file.name,
                    correlationId: generateCorrelationId(),
                  },
                  (response: FilePayload | ErrorPayload) => {
                    if ('error' in response) {
                      reject(new Error(response.error.message));
                    } else {
                      resolve(response.id);
                    }
                  }
                );
              };
              reader.onerror = () => reject(new Error('Failed to read file'));
              reader.readAsArrayBuffer(file);
            });
          });

          const results = await Promise.allSettled(uploadPromises);
          const errors = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected');
          const successIds = results
            .filter((r): r is PromiseFulfilledResult<string> => r.status === 'fulfilled')
            .map((r) => r.value);

          if (errors.length > 0) {
            const messages = errors.map((e) => (e.reason as Error).message);
            console.warn('Some file uploads failed:', messages);
            set((state) => ({
              chat: {
                value: {
                  ...state.chat!.value!,
                  sending: successIds.length === 0 ? false : state.chat!.value!.sending,
                },
              },
              uploadWarning: messages.join('\n'),
            }));
          }

          return successIds;
        };

        uploadFiles()
          .then((fileUploads) => {
            if (attachments && attachments.length > 0 && fileUploads.length === 0) {
              set((state) => ({
                chat: { value: { ...state.chat!.value!, sending: false } },
                pendingText: message,
              }));
              return;
            }
            console.log('Uploaded File IDs:', fileUploads);
            const currentNudgeId = get().hostPlatformContext?.nudge?.nudgeId;
            if (currentNudgeId) {
              set((state) => ({
                hostPlatformContext: state.hostPlatformContext
                  ? { ...state.hostPlatformContext, nudge: undefined }
                  : undefined,
              }));
            }
            if (!chatValue.chat) {
              const sharingConfirmed = !!get().chat?.value?.sharingConfirmed;
              const shared = getSharingConfirmationSupported(get()) ? sharingConfirmed : true;

              socket.emit(
                'chat:create',
                {
                  actionId,
                  message: message || '',
                  display,
                  attachments: fileUploads,
                  shared,
                  isAutoMessage,
                  nudgeId: currentNudgeId,
                  correlationId: generateCorrelationId(),
                },
                wrapHandler((payload) => {
                  maybeCacheConversation(payload.chat.id);
                  set((state) => {
                    return {
                      chat: {
                        value: {
                          ...state.chat?.value,
                          chat: payload.chat,
                          messages: [{ ...payload.message, attachments: fileUploads }],
                          progressingBotMessage: {
                            content: '',
                            replyToMessageId: payload.message.id,
                          },
                          sending: false,
                        },
                      },
                    };
                  });
                })
              );
            } else {
              socket.emit(
                'chat-msg:create',
                {
                  chatId: chatValue.chat.id,
                  request: {
                    message: message || '',
                    display,
                    attachments: fileUploads,
                    isAutoMessage,
                    nudgeId: currentNudgeId,
                  },
                  correlationId: generateCorrelationId(),
                },
                wrapHandler((payload) => {
                  set((state) => {
                    const existingValue = state.chat!.value!;
                    return {
                      chat: {
                        value: {
                          ...existingValue,
                          messages: [
                            ...existingValue.messages,
                            {
                              ...payload,
                              attachments: fileUploads,
                            },
                          ],
                          progressingBotMessage:
                            payload.role === 'system' || ('to' in payload && payload.to === 'ai')
                              ? { content: '', replyToMessageId: payload.id }
                              : undefined,
                          sending: false,
                        },
                      },
                    };
                  });
                })
              );
            }
          })
          .catch((error) => {
            console.error('File upload error:', error);
            set((state) => ({
              chat: {
                value: {
                  ...state.chat!.value!,
                  sending: false,
                },
              },
              uploadWarning: `File upload failed: ${(error as Error).message}`,
            }));
          });
      }

      function setChatSharingConfirmed(sharingConfirmed: boolean) {
        const { chatValue } = getActiveChat(get());

        if (chatValue.messages.length) {
          throw new Error('Cannot change sharing confirmation after first message');
        }

        if (!getSharingConfirmationSupported(get())) {
          throw new Error('Sharing confirmation is not supported');
        }

        set((state) => ({
          chat: {
            value: {
              ...state.chat!.value!,
              sharingConfirmed,
            },
          },
        }));
      }

      function setMessageLiked(
        messageId: string,
        isLiked: boolean,
        comment: string,
        onSuccess: (isLiked: boolean) => void
      ) {
        const { chatValue, socket } = getActiveChat(get());
        const lastAiMessage = chatValue.messages.findLast(({ role }) => role === 'ai');

        if (!chatValue.chat?.id) {
          throw new Error(`Missing Chat id for sending feedback`);
        }
        if (lastAiMessage?.role !== 'ai' || lastAiMessage.id !== messageId) {
          throw new Error(`Can only send feedback on the last ai message`);
        }
        if (lastAiMessage.feedbacks.length && lastAiMessage.feedbacks.length > 0) {
          throw new Error(`Feedback on this message already exists`);
        }

        socket.emit(
          'chat-msg-feedback:create',
          {
            chatId: chatValue.chat.id,
            messageId,
            body: { isHelpful: isLiked, comment },
            correlationId: generateCorrelationId(),
          },
          wrapHandler((payload) => {
            onSuccess(payload.isHelpful);
            set((state) => {
              const existingValue = state.chat!.value!;
              const updatedMessages = chatValue.messages.map((msg) => {
                if (msg.id === payload.messageId && msg.role === 'ai') {
                  return { ...msg, feedbacks: [...msg.feedbacks, payload] };
                }
                return msg;
              });

              return { chat: { value: { ...existingValue, messages: updatedMessages } } };
            });
            return;
          })
        );
      }

      function getFileMetadata(fileId: string): Promise<FilePayload> {
        const { socket } = getActiveChat(get());

        return new Promise((resolve, reject) => {
          socket.emit(
            'chat.file-metadata',
            { fileId, correlationId: generateCorrelationId() },
            (response: FilePayload | ErrorPayload) => {
              if ('error' in response) {
                reject(new Error(response.error.message));
              } else {
                resolve(response);
              }
            }
          );
        });
      }

      function getSourceFile(
        chatId: string,
        messageId: string,
        sourceId: string
      ): Promise<FilePayload> {
        const { socket } = getActiveChat(get());

        return new Promise((resolve, reject) => {
          socket.emit(
            'chat.source-file',
            { chatId, messageId, sourceId, correlationId: generateCorrelationId() },
            (response: FilePayload | ErrorPayload) => {
              if ('error' in response) {
                reject(new Error(response.error.message));
              } else {
                resolve(response);
              }
            }
          );
        });
      }

      return {
        hostPlatformContext: undefined,
        error: undefined,
        uploadWarning: undefined,
        pendingText: undefined,
        app: { loading: true },
        chats: [],
        chat: undefined,
        initializing: false,
        openChat,
        startNewChat,
        closeChat,
        setChatSharingConfirmed,
        sendMessage,
        setMessageLiked,
        init,
        getFileMetadata,
        getSourceFile,
      };
    }),
    {
      name: `chat-storage:state:${widgetKey}:${deviceId}`,
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => {
        if (state.app.value?.type !== 'widget') {
          return {};
        }
        const isNewAiLayer = !!state.app.value?.amaAction?.useNewAiLayer;
        const persistConversation = state.app.value?.persistConversation !== false;
        const keys =
          !isNewAiLayer || persistConversation ? ['app', 'chats', 'chat'] : ['app', 'chats'];
        return Object.fromEntries(Object.entries(state).filter(([key]) => keys.includes(key)));
      },
    }
  )
);

export default useChatStore;

function chatSorter(a: ChatSummaryItem, b: ChatSummaryItem) {
  return a.summary.lastMessageAt > b.summary.lastMessageAt ? 1 : -1;
}

function getActiveChat({ hostPlatformContext, chat }: Store) {
  const socket = hostPlatformContext?.socket;
  const chatValue = chat?.value;
  if (!socket) {
    throw new Error('Socket not started');
  }
  if (!chatValue) {
    throw new Error('Cannot send message, there is no chat active');
  }

  return { chatValue, socket };
}

function getSharingConfirmationSupported({ app, hostPlatformContext }: Store) {
  return (
    !!app.value?.groupingSupported &&
    !!hostPlatformContext?.groupingId &&
    !!app.value?.sharingApprovalRequired
  );
}
